import React, { useContext, useState } from "react";
import Image from "mui-image";
import Avatar from "@mui/material/Avatar";
import Link from "@mui/material/Link";
import ListItemButton from "@mui/material/ListItemButton";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Badge from "@mui/material/Badge";
import Settings from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import { useLocation, useNavigate } from "react-router-dom";
import LogoutButton from "../login/LogoutButton";
import { Path } from "../Path";
import { Link as RouterLink } from "react-router-dom";
import { AccountContext } from "./AccountProvider";
import { useInvitesForUser } from "@seabrookstudios/pitch2table";
import { usePublisherAndContacts } from "@seabrookstudios/pitch2table";
import logo from "../assets/logo.png";
import { GoMobileButton } from "../general/GetMobilePage";
import { FeedbackAndSupportButton } from "../general/FeedbackAndSupportPage";
import { SideMenuOption } from "./SideMenuOption";
import { UpdatesButton } from "../general/UpdatesPage";
import Article from "@mui/icons-material/Article";
import Money from "@mui/icons-material/Money";
import Box from "@mui/material/Box";
import { Features } from "../games/features";
import Casino from "@mui/icons-material/Casino";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { SearchButton } from "./SearchButton";
import { ExternalLink } from "../styleguide/AppLink";
import Event from "@mui/icons-material/Event";
import Dashboard from "@mui/icons-material/Dashboard";
import SwitchAccount from "@mui/icons-material/SwitchAccount";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

/**
 * @param {string} label
 * @param {string} tooltip
 * @param {import("react-router-dom").To} to
 * @param {import("@mui/material/OverridableComponent").OverridableComponent<import("@mui/material").SvgIconTypeMap<{}, "svg">>} Icon
 */
const makeMenuOption = (label, tooltip, to, Icon) => {
  /**
   * @param {object} props
   * @param {boolean} [props.iconOnly]
   */
  const MenuOption = ({ iconOnly = false }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const onClickHandler = () => {
      const target = typeof to === "string" ? { pathname: to } : to;
      // IMPORTANT: `to` objects are created at module-load time (e.g. `Path.games()`),
      // so their `.search` can be stale. Always preserve the *current* query params.
      navigate({ pathname: target.pathname, search: location.search });
    };

    return (
      <SideMenuOption
        onClick={onClickHandler}
        tooltip={tooltip}
        label={label}
        selected={location.pathname.startsWith((typeof to === "string" ? to : to.pathname) || "")}
        iconOnly={iconOnly}
      >
        <Icon fontSize={Features.BaseCampLayout ? "small" : "medium"} />
      </SideMenuOption>
    );
  };

  return MenuOption;
};

const PublisherHomeButton = () => {
  const location = useLocation();

  const { publisherId } = useContext(AccountContext);
  const { data: publisherAndContacts = [] } = usePublisherAndContacts(publisherId);
  const publisher = publisherAndContacts[0];

  return (
    <ListItemButton
      sx={{ display: "flex", padding: 1, justifyContent: "center", alignItems: "center" }}
      selected={location.pathname === "/"}
    >
      <Link
        color="inherit"
        component={RouterLink}
        to={Path.Home}
        aria-label="go publisher home"
        aria-controls="publisher home"
        sx={{ flexGrow: 1 }}
      >
        <Avatar src={publisher ? publisher.logoUrl : ""} />
      </Link>
    </ListItemButton>
  );
};

const UserHomeButton = makeMenuOption("Games", "Head back to your dashboard", Path.games(), () => (
  <Image alt="My Games" src={logo} fit="contain" width={32} height={32} />
));

const MyDashboard = makeMenuOption("Dashboard", "View your dashboard", Path.dashboard(), () => <Dashboard />);

const Pitch2TableMenu = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(/** @type {HTMLButtonElement|null} */ (null));
  const open = Boolean(anchorEl);

  /**
   * @param {React.MouseEvent<HTMLButtonElement>} event
   */
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * @param {import("react-router-dom").To} path
   */
  const handleCloseAndNavigate = (path) => () => {
    navigate(path);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        id="app-menu"
        aria-controls={open ? "app-menu-overlay" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <Image alt="Pitch2Table" src={logo} fit="contain" width={32} height={32} />
      </Button>
      <Menu
        id="app-menu-overlay"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "app-menu",
        }}
      >
        <MenuItem onClick={handleCloseAndNavigate(Path.Mobile)}>Go Mobile</MenuItem>
        <MenuItem onClick={handleCloseAndNavigate(Path.Support)}>Feedback & Support</MenuItem>
        <MenuItem onClick={handleCloseAndNavigate(Path.Updates)}>Latest Updates</MenuItem>
      </Menu>
    </div>
  );
};

const Pitch2TableButton = () => {
  return <Pitch2TableMenu />;
};

const SwitchAccountLink = () => (
  <IconButton component={RouterLink} to={Path.SelectAccount}>
    <Icon>
      <SwitchAccount />
    </Icon>
  </IconButton>
);

const MyGames = makeMenuOption("Games", "View your games", Path.games(), () => <Casino />);
const MyPublishers = makeMenuOption("Publishers", "View your publishers", Path.publishers(), () => <BusinessIcon />);
const MyDocuments = makeMenuOption("Contracts", "View your contracts", Path.documents(), () => <Article />);
const MyRoyalties = makeMenuOption("Royalties", "View your royalties", Path.royalties().none(), () => <Money />);
const MyEvents = makeMenuOption("Calendar", "View events and meetings", Path.events().none(), () => <Event />);

const UserSettingsButton = ({ iconOnly = false }) => {
  const { data: invites = [] } = useInvitesForUser();
  const location = useLocation();

  const navigate = useNavigate();

  const onClickHandler = () => {
    navigate(Path.settings().none());
  };

  return (
    <SideMenuOption
      id="settings-menu"
      label="Settings"
      tooltip="Edit account and application settings"
      onClick={onClickHandler}
      selected={location.pathname.startsWith(Path.settings().none())}
      iconOnly={iconOnly}
    >
      <Badge color="primary" badgeContent={invites.length}>
        <Settings />
      </Badge>
    </SideMenuOption>
  );
};

const PublisherSettingsButton = () => {
  const location = useLocation();

  return (
    <ListItemButton
      sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      selected={location.pathname.startsWith(Path.Settings)}
    >
      <Link
        color="inherit"
        component={RouterLink}
        to={Path.Settings}
        aria-label="go to publisher settings"
        aria-controls="publisher settings"
        sx={{ flexGrow: 1 }}
      >
        <Settings />
      </Link>
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
const UserMenu = () => {
  const { mode } = useContext(AccountContext);

  return (
    <Paper style={{ minHeight: "100vh", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}>
      <Stack sx={{ width: { xs: 60, md: 175, lg: 200 } }} direction="column">
        <Stack
          spacing={2}
          pt={2}
          pb={2}
          sx={{
            alignItems: { xs: "center", lg: "flex-start" },
            mt: "auto",
            flexGrow: 1,
          }}
        >
          <MyDashboard />
          {mode === "Publisher" ? <PublisherHomeButton /> : <UserHomeButton />}
          <MyPublishers />
          <MyDocuments />
          <MyRoyalties />
          <MyEvents />
          {/* {mode === "Designer" && <MyGamesButton />} */}
          {mode === "Publisher" ? <PublisherSettingsButton /> : <UserSettingsButton />}
          <GoMobileButton />
          <FeedbackAndSupportButton />
          <UpdatesButton />
          <LogoutButton />
        </Stack>
      </Stack>
    </Paper>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
export const UserMenuTop = () => {
  return (
    <Box width="100%">
      <Stack alignItems="center" justifyContent="space-between" direction="row">
        <Stack direction="row" flex="1" alignItems="center" spacing={1}>
          <Pitch2TableButton />
          {Features.SwitchAccount && <SwitchAccountLink />}
        </Stack>
        <Stack alignItems="center" justifyContent="space-evenly" direction="row" flex="2">
          <MyDashboard />
          <MyGames />
          <MyPublishers />
          {/* <MyPlayTests /> */}
          <MyDocuments />
          <MyRoyalties />
          <MyEvents />
        </Stack>
        <Stack direction="row" flex="1" justifyContent="flex-end">
          <SearchButton />
          <UserSettingsButton iconOnly />
        </Stack>
      </Stack>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId} props.publisherId
 */
export const UserMenuTopPublic = () => {
  return (
    <Box width="100%">
      <Stack direction="row">
        <ListItemButton sx={{ padding: 1, flex: 0 }}>
          <ExternalLink href="https://pitch2table.com">
            <Image alt="Pitch2Table" src={logo} fit="contain" width={32} height={32} />
          </ExternalLink>
        </ListItemButton>
      </Stack>
    </Box>
  );
};

export default UserMenu;
